<script setup>
import { ref, watch, computed, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import aiAnalyticsApi from 'dashboard/api/aiAnalytics';
import aiAuditsApi from 'dashboard/api/aiAudits';
import conversationApi from 'dashboard/api/conversations';

const props = defineProps({
  since: { type: Number, required: true },
  until: { type: Number, required: true },
});

const route  = useRoute();
const router = useRouter();

const loading = ref(false);
const data    = ref(null);
const scoreFilter = ref('all'); // 'all' | 'high' | 'mid' | 'low'
const page = ref(1);

const reprocessing = ref(false);
const reprocessMsg = ref(null);
const reprocessPollTimer = ref(null);
const severityFilter = ref('alta'); // 'alta' | 'all'

function stopReprocessPoll() {
  if (reprocessPollTimer.value) {
    clearInterval(reprocessPollTimer.value);
    reprocessPollTimer.value = null;
  }
}

onUnmounted(stopReprocessPoll);

// Nomes de contato: convId → nome
const convNames          = ref({});
const violationModalNames = ref({});

async function fetchNames(ids, target) {
  await Promise.all(
    ids.map(async id => {
      if (target.value[id] !== undefined) return;
      try {
        const { data: conv } = await conversationApi.show(id);
        target.value[id] = conv.meta?.sender?.name || null;
      } catch {
        target.value[id] = null;
      }
    })
  );
}

// Modal de violações
const violationModal = ref(null); // { categoria, severidade, conversation_ids }
const violationModalPage = ref(1);
const MODAL_PER_PAGE = 20;

const violationModalPages = computed(() =>
  Math.ceil((violationModal.value?.conversation_ids?.length ?? 0) / MODAL_PER_PAGE)
);
const violationModalItems = computed(() => {
  if (!violationModal.value) return [];
  const start = (violationModalPage.value - 1) * MODAL_PER_PAGE;
  return violationModal.value.conversation_ids.slice(start, start + MODAL_PER_PAGE);
});

function openViolationModal(v) {
  violationModal.value = v;
  violationModalPage.value = 1;
  violationModalNames.value = {};
  fetchNames(v.conversation_ids || [], violationModalNames);
}
function closeViolationModal() {
  violationModal.value = null;
}

const totalPages = computed(() => {
  if (!data.value) return 1;
  return Math.ceil((data.value.all_conversations_total ?? 0) / (data.value.all_conversations_per_page ?? 20));
});

async function reprocessAll() {
  if (!confirm(`Reprocessar as auditorias do período selecionado com o prompt atual? Isso pode levar alguns minutos.`)) return;
  reprocessing.value = true;
  reprocessMsg.value = null;
  stopReprocessPoll();
  try {
    const { data: res } = await aiAuditsApi.reprocessAll(route.params.accountId, {
      since: props.since,
      until: props.until,
    });
    reprocessMsg.value = `${res.total} enfileiradas. Atualizando…`;

    // Auto-atualiza dados a cada 20s enquanto auditorias chegam (máx 3 min)
    let polls = 0;
    reprocessPollTimer.value = setInterval(async () => {
      try { await load(); } catch { /* ignora falhas transitórias */ }
      polls += 1;
      if (polls >= 9) {
        stopReprocessPoll();
        reprocessMsg.value = null;
      }
    }, 20000);
  } catch {
    reprocessMsg.value = 'Erro ao enfileirar. Tente novamente.';
  } finally {
    reprocessing.value = false;
  }
}

async function load() {
  loading.value = true;
  convNames.value = {};
  try {
    const scoreParam = scoreFilter.value !== 'all' ? scoreFilter.value : undefined;
    const { data: res } = await aiAnalyticsApi.getAuditStats(
      route.params.accountId,
      { since: props.since, until: props.until, page: page.value, score_filter: scoreParam, severity_filter: severityFilter.value }
    );
    data.value = res;
    const ids = (res.all_conversations || []).map(c => c.conversation_id);
    fetchNames(ids, convNames);
  } finally {
    loading.value = false;
  }
}

watch(() => [props.since, props.until], () => { stopReprocessPoll(); reprocessMsg.value = null; page.value = 1; load(); }, { immediate: true });

watch(scoreFilter, () => { page.value = 1; load(); });
watch(severityFilter, () => { page.value = 1; load(); });

async function validateConv(convId) {
  try {
    await aiAuditsApi.validate(route.params.accountId, convId);
    const conv = data.value?.all_conversations?.find(c => c.conversation_id === convId);
    if (conv) conv.validated_at = new Date().toISOString();
  } catch { /* silent */ }
}

function goToPage(n) {
  page.value = n;
  load();
}

const bucketLabel = b => {
  const labels = { 1: '0–2', 2: '2–4', 3: '4–6', 4: '6–8', 5: '8–10' };
  return labels[b] ?? b;
};

const maxHist = computed(() => {
  if (!data.value?.score_histogram?.length) return 1;
  return Math.max(...data.value.score_histogram.map(r => r.count));
});

const histogramBars = computed(() => {
  const hist = data.value?.score_histogram ?? [];
  return [1, 2, 3, 4, 5].map(b => {
    const found = hist.find(h => h.bucket === b);
    return { bucket: b, count: found?.count ?? 0 };
  });
});

const histTotal = computed(() =>
  data.value?.score_histogram?.reduce((s, h) => s + h.count, 0) ?? 0
);

function scoreClass(s) {
  if (!s && s !== 0) return 'text-slate-400';
  if (s >= 8) return 'text-emerald-500';
  if (s >= 6) return 'text-yellow-500';
  return 'text-red-500';
}

function scoreBg(s) {
  if (!s && s !== 0) return 'bg-slate-100 dark:bg-slate-700';
  if (s >= 8) return 'bg-emerald-50 dark:bg-emerald-900/20';
  if (s >= 6) return 'bg-yellow-50 dark:bg-yellow-900/20';
  return 'bg-red-50 dark:bg-red-900/20';
}

function sevClass(sev) {
  if (sev === 'alta')  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  if (sev === 'media') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
}

function gatilhoClass(g) {
  if (g === 'transfer') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
  if (g === 'despedida') return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
  return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
}

function gatilhoLabel(g) {
  if (g === 'transfer') return 'Transferência';
  if (g === 'despedida') return 'Despedida';
  if (g === 'manual') return 'Manual';
  return g ?? '—';
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function goToConv(id) {
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: id },
  });
}

const FILTERS = [
  { key: 'all',  label: 'Todas' },
  { key: 'high', label: 'Excelentes ≥ 8' },
  { key: 'mid',  label: 'Boas 6–8' },
  { key: 'low',  label: 'Críticas < 6' },
];
</script>

<template>
  <div class="flex flex-col gap-5">
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-16">
      <span class="i-lucide-loader-circle animate-spin text-3xl text-woot-500" />
    </div>

    <template v-else-if="data">
      <!-- Barra de ações -->
      <div class="flex items-center justify-between flex-wrap gap-2">
        <p class="text-xs text-slate-400">
          {{ data.all_conversations_total ?? 0 }} auditorias no período
        </p>
        <div class="flex items-center gap-2">
          <span v-if="reprocessMsg" class="text-xs text-slate-500">{{ reprocessMsg }}</span>
          <button
            class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-woot-400 hover:text-woot-600 dark:hover:text-woot-400 transition-colors disabled:opacity-50"
            :disabled="reprocessing"
            @click="reprocessAll"
          >
            <span :class="reprocessing ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-refresh-cw'" class="text-sm" />
            {{ reprocessing ? 'Enfileirando…' : 'Reprocessar todas' }}
          </button>
        </div>
      </div>

      <!-- Score por agente -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div
          v-for="a in data.by_agent"
          :key="a.agente"
          class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
        >
          <div class="flex items-center justify-between mb-3">
            <div>
              <p class="font-semibold text-sm text-slate-800 dark:text-slate-100">{{ a.agente }}</p>
              <p class="text-xs text-slate-400">{{ a.unidade }}</p>
            </div>
            <span
              class="text-2xl font-bold tabular-nums"
              :class="scoreClass(a.avg_score)"
            >{{ a.avg_score?.toFixed(1) ?? '—' }}</span>
          </div>
          <div class="flex items-center gap-3 text-xs text-slate-500">
            <span>{{ a.total }} auditorias</span>
            <span class="text-emerald-500">{{ a.high_count }} excelentes</span>
            <span class="text-red-400">{{ a.low_count }} críticas</span>
          </div>
        </div>

        <div
          v-if="!data.by_agent?.length"
          class="col-span-3 text-center py-8 text-slate-400 text-sm"
        >
          Nenhuma auditoria no período selecionado.
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <!-- Histograma de scores (barras horizontais) -->
        <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <p class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">
            Distribuição de scores
          </p>
          <div class="space-y-2.5">
            <div
              v-for="bar in histogramBars"
              :key="bar.bucket"
              class="flex items-center gap-3"
            >
              <span class="text-[11px] font-mono text-slate-400 w-9 shrink-0 text-right">
                {{ bucketLabel(bar.bucket) }}
              </span>
              <div class="flex-1 h-5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all duration-500"
                  :class="bar.bucket >= 4 ? 'bg-green-500' : bar.bucket === 3 ? 'bg-yellow-400' : bar.bucket === 2 ? 'bg-yellow-600' : 'bg-red-500'"
                  :style="{ width: maxHist > 0 ? `${Math.max(2, (bar.count / maxHist) * 100)}%` : '0%' }"
                />
              </div>
              <span class="text-[11px] tabular-nums text-slate-500 dark:text-slate-400 w-16 shrink-0">
                {{ bar.count.toLocaleString('pt-BR') }}
                <span class="text-slate-400 dark:text-slate-600 text-[10px]">
                  ({{ histTotal > 0 ? Math.round(bar.count / histTotal * 100) : 0 }}%)
                </span>
              </span>
            </div>
          </div>
          <p v-if="!data.score_histogram?.length" class="text-xs text-slate-400 text-center py-4">Sem dados</p>
        </div>

        <!-- Top violações -->
        <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <div class="flex items-center justify-between mb-4 gap-2">
            <p class="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Top categorias de violação
            </p>
            <div class="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-full p-0.5">
              <button
                class="px-2.5 py-0.5 text-[10px] font-semibold rounded-full transition-all"
                :class="severityFilter === 'alta' ? 'bg-white dark:bg-slate-700 text-red-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'"
                @click="severityFilter = 'alta'"
              >Só Alta</button>
              <button
                class="px-2.5 py-0.5 text-[10px] font-semibold rounded-full transition-all"
                :class="severityFilter === 'all' ? 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 shadow-sm' : 'text-slate-400 hover:text-slate-600'"
                @click="severityFilter = 'all'"
              >Todas</button>
            </div>
          </div>
          <div v-if="data.top_violations?.length" class="space-y-1.5">
            <button
              v-for="v in data.top_violations"
              :key="`${v.categoria}-${v.severidade}`"
              class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group text-left"
              @click="openViolationModal(v)"
            >
              <span
                class="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase shrink-0"
                :class="sevClass(v.severidade)"
              >{{ v.severidade }}</span>
              <span class="text-xs text-slate-600 dark:text-slate-300 flex-1 truncate">
                {{ v.categoria?.replace(/_/g, ' ') }}
              </span>
              <span class="text-xs font-semibold text-slate-500 shrink-0 tabular-nums">{{ v.total }}×</span>
              <span class="i-lucide-external-link text-xs text-slate-300 dark:text-slate-600 group-hover:text-woot-500 transition-colors shrink-0" />
            </button>
          </div>
          <p v-else class="text-xs text-slate-400 text-center py-8">Sem violações no período</p>
        </div>
      </div>

      <!-- Todas as conversas auditadas -->
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
        <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p class="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Conversas auditadas
            <span class="ml-1.5 text-xs font-normal text-slate-400">
              ({{ data.all_conversations_total ?? 0 }})
            </span>
          </p>

          <!-- Filtros por score -->
          <div class="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-full p-0.5">
            <button
              v-for="f in FILTERS"
              :key="f.key"
              class="px-3 py-1 text-xs font-medium rounded-full transition-all duration-150"
              :class="scoreFilter === f.key
                ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
              @click="scoreFilter = f.key"
            >
              {{ f.label }}
            </button>
          </div>
        </div>

        <div v-if="data.all_conversations?.length" class="overflow-x-auto">
          <table class="w-full text-xs">
            <thead>
              <tr class="text-left text-slate-400 border-b border-slate-100 dark:border-slate-800">
                <th class="pb-2 font-medium">Contato</th>
                <th class="pb-2 font-medium">Data</th>
                <th class="pb-2 font-medium">Agente</th>
                <th class="pb-2 font-medium">Unidade</th>
                <th class="pb-2 font-medium">Score</th>
                <th class="pb-2 font-medium">Gatilho</th>
                <th class="pb-2 font-medium">Recomendação</th>
                <th class="pb-2 font-medium pr-2"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50 dark:divide-slate-800">
              <tr
                v-for="c in data.all_conversations"
                :key="c.conversation_id"
                class="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer"
                :class="c.validated_at ? 'opacity-50' : ''"
                @click="goToConv(c.conversation_id)"
              >
                <td class="py-2">
                  <span class="text-woot-600 dark:text-woot-400 font-medium block leading-tight">
                    {{ convNames[c.conversation_id] || '#' + c.conversation_id }}
                  </span>
                  <span v-if="convNames[c.conversation_id]" class="text-[10px] text-slate-400">
                    #{{ c.conversation_id }}
                  </span>
                </td>
                <td class="py-2 text-slate-400 whitespace-nowrap">{{ formatDate(c.audited_at) }}</td>
                <td class="py-2 text-slate-600 dark:text-slate-300">{{ c.agente }}</td>
                <td class="py-2 text-slate-500">{{ c.unidade }}</td>
                <td class="py-2">
                  <span
                    class="inline-block px-1.5 py-0.5 rounded font-bold tabular-nums"
                    :class="[scoreClass(c.score_geral), scoreBg(c.score_geral)]"
                  >{{ c.score_geral?.toFixed(1) ?? '—' }}</span>
                </td>
                <td class="py-2">
                  <span
                    class="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase"
                    :class="gatilhoClass(c.gatilho)"
                  >{{ gatilhoLabel(c.gatilho) }}</span>
                </td>
                <td class="py-2 text-slate-400 max-w-xs truncate">{{ c.recomendacao ?? '—' }}</td>
                <td class="py-2 pr-2 text-right" @click.stop>
                  <span v-if="c.validated_at" class="flex items-center justify-end gap-1 text-[10px] text-emerald-500">
                    <span class="i-lucide-check-circle-2" />
                    Corrigida
                  </span>
                  <button
                    v-else
                    class="text-[10px] text-slate-400 hover:text-emerald-500 transition-colors flex items-center gap-0.5"
                    title="Marcar como corrigida"
                    @click="validateConv(c.conversation_id)"
                  >
                    <span class="i-lucide-check-circle text-sm" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Paginação -->
          <div v-if="totalPages > 1" class="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
            <p class="text-xs text-slate-400">
              Página {{ page }} de {{ totalPages }}
            </p>
            <div class="flex items-center gap-1">
              <button
                class="px-2.5 py-1 text-xs rounded border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 disabled:opacity-40 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                :disabled="page <= 1 || loading"
                @click="goToPage(page - 1)"
              >
                ← Anterior
              </button>
              <button
                v-for="p in totalPages"
                :key="p"
                class="w-7 h-7 text-xs rounded border transition-colors"
                :class="p === page
                  ? 'border-woot-500 bg-woot-500 text-white font-semibold'
                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'"
                :disabled="loading"
                @click="goToPage(p)"
              >
                {{ p }}
              </button>
              <button
                class="px-2.5 py-1 text-xs rounded border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 disabled:opacity-40 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                :disabled="page >= totalPages || loading"
                @click="goToPage(page + 1)"
              >
                Próxima →
              </button>
            </div>
          </div>
        </div>
        <p v-else class="text-xs text-slate-400 text-center py-6">
          Nenhuma conversa neste filtro.
        </p>
      </div>
    </template>
  </div>

  <!-- Modal de conversas da violação -->
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="violationModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="closeViolationModal"
      >
        <div class="absolute inset-0 bg-black/50" @click="closeViolationModal" />
        <div class="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">
          <!-- Header -->
          <div class="flex items-start justify-between p-5 border-b border-slate-100 dark:border-slate-800">
            <div>
              <div class="flex items-center gap-2 mb-1">
                <span
                  class="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase"
                  :class="sevClass(violationModal.severidade)"
                >{{ violationModal.severidade }}</span>
                <p class="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {{ violationModal.categoria?.replace(/_/g, ' ') }}
                </p>
              </div>
              <p class="text-xs text-slate-400">
                {{ violationModal.total }} ocorrências —
                {{ violationModal.conversation_ids?.length ?? 0 }} conversas com esta violação
              </p>
            </div>
            <button
              class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors ml-4 shrink-0"
              @click="closeViolationModal"
            >
              <span class="i-lucide-x text-lg" />
            </button>
          </div>

          <!-- Lista de conversas -->
          <div class="flex-1 overflow-y-auto p-4">
            <div v-if="violationModalItems.length" class="space-y-1">
              <button
                v-for="convId in violationModalItems"
                :key="convId"
                class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left group"
                @click="goToConv(convId); closeViolationModal()"
              >
                <span class="i-lucide-message-square text-slate-300 dark:text-slate-600 group-hover:text-woot-500 transition-colors shrink-0" />
                <span class="flex-1 min-w-0">
                  <span class="text-sm font-medium text-woot-600 dark:text-woot-400 block truncate">
                    {{ violationModalNames[convId] || 'Conversa #' + convId }}
                  </span>
                  <span v-if="violationModalNames[convId]" class="text-[11px] text-slate-400">
                    #{{ convId }}
                  </span>
                </span>
                <span class="i-lucide-arrow-right text-xs text-slate-300 dark:text-slate-600 group-hover:text-woot-500 transition-colors ml-auto shrink-0" />
              </button>
            </div>
            <p v-else class="text-xs text-slate-400 text-center py-8">
              Nenhuma conversa disponível
            </p>
          </div>

          <!-- Paginação do modal -->
          <div
            v-if="violationModalPages > 1"
            class="flex items-center justify-between p-4 border-t border-slate-100 dark:border-slate-800"
          >
            <p class="text-xs text-slate-400">
              Página {{ violationModalPage }} de {{ violationModalPages }}
            </p>
            <div class="flex items-center gap-1">
              <button
                class="px-2.5 py-1 text-xs rounded border border-slate-200 dark:border-slate-700 text-slate-500 disabled:opacity-40 hover:border-slate-300 transition-colors"
                :disabled="violationModalPage <= 1"
                @click="violationModalPage--"
              >
                ← Anterior
              </button>
              <button
                class="px-2.5 py-1 text-xs rounded border border-slate-200 dark:border-slate-700 text-slate-500 disabled:opacity-40 hover:border-slate-300 transition-colors"
                :disabled="violationModalPage >= violationModalPages"
                @click="violationModalPage++"
              >
                Próxima →
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>
